import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  MouseEvent,
} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import multiMonthPlugin from "@fullcalendar/multimonth";
import listPlugin from "@fullcalendar/list";
import {
  DateSelectArg,
  DateSpanApi,
  EventClickArg,
  EventDropArg,
} from "@fullcalendar/core/index.js";
import BookingForm from "./BookingForm";
import usePopup from "../../../hooks/usePopup";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  ListItemText,
  Menu,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  BookingFormType,
  BookingListType,
  prepareBookingParams,
  buildCountryCodeFromBooking,
  SearchParamsDoctorType,
  // SearchParamsDoctorType,
} from "./types";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation } from "react-query";
import { cancelBooking, getAllBookings, updateBookings } from "./services";
import momentPlugin from "@fullcalendar/moment";
import moment from "moment";
// import PersonIcon from "@mui/icons-material/Person";
import { useNavigate } from "react-router-dom";
import moduleRoutes from "../../../routes/moduleRouteList";
import patientCareRouteList from "../../routeList";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import useBookingSave from "./apiHooks";
import { calculateAge } from "../../../utilis/methods/dateMethods";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { PatientBookingContext } from "./context/PatientBookingProvider";
// import debounce from "../../../utilis/methods/networkHandling";
type CopyType = "single" | "multi" | null;
let copyString: CopyType = null;
const base = (window as any)._env_?.BASE_URL;
let updateFn = () => {};
function CalenderComponent() {
  const theme = useTheme();
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const skipDetailsCloseRef = useRef(false);
  const selectedBookingIdRef = useRef<string | number | null>(null);
  const { showSnackbar, CustomModal } = usePopup();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [copy, setCopy] = useState<CopyType>(null);
  const [copyOpen, setCopyOpen] = useState<boolean>(false);
  const [backupArray, setBackupArray] = useState<BookingListType[]>([]);
  const [confirm, setConfirm] = useState(false);
  // let selected:{id:string | null,startDate:string|null,endDate:string|null,view:string} = {
  //   id:null,
  //   startDate:null,
  //   endDate:null,
  //   view:""
  // }
  const {
    setBookingList,
    bookingList,
    setTimeSlot,
    open,
    setOpen,
    refetch,
    setRefetch,
    editDetails,
    setEditDetails,
    setBookingSearchParams,
    bookingSearchParam,
    setCopyId,
  } = useContext(PatientBookingContext);

  const { mutate: fetchAllBooking, isLoading: bookingLoading } = useMutation(
    getAllBookings,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          setBookingList(result.response as BookingListType[]);
          setRefetch(false);
          if (selectedBookingIdRef.current) {
            skipDetailsCloseRef.current = true;
          }
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
    }
  );
  const { mutate: bookingUpdate } = useMutation(updateBookings, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        setRefetch(true);
        setConfirm(false);
      } else {
        showSnackbar({
          message: result?.responseMessage || "Error !",
          open: true,
          variant: "warning",
          duration: 2000,
        });
      }
    },
    onError: (e: any) => {
      showSnackbar({
        message: e?.response?.data?.responseMessage || "Something went wrong !",
        open: true,
        variant: "warning",
        duration: 2000,
      });
      setRefetch(true);
    },
  });

  const { mutate: bookingCancel, isLoading: cancelLoading } = useMutation(
    cancelBooking,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "fail") {
          setRefetch(true);
        } else {
          const updated = bookingList.filter(
            (item) => item.id != editDetails?.id
          );
          setBookingList(updated);
          showSnackbar({
            message: "Booking cancelled successfully !",
            variant: "success",
            open: true,
            duration: 2000,
          });
          handleCloseDetails();
        }
      },
      onError: (e: any) => {
        setRefetch(true);

        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Something went wrong !",
          variant: "warning",
          open: true,
          duration: 2000,
        });
      },
    }
  );

  const onSaveSuccess = () => {
    if (copy) {
      if (copy == "single") {
        setCopy(null);
        setEditDetails(null);
      }
    } else {
      setEditDetails(null);
    }
  };

  const { mutate: save, isLoading: saveLoading } =
    useBookingSave(onSaveSuccess);

  const handleDayCellDidMount = () => {
    const popovers = document.querySelectorAll(".fc-more-popover");
    popovers.forEach((popover) => {
      (popover as HTMLElement).style.maxHeight = "140px";
      (popover as HTMLElement).style.width = "fit-content";
      (popover as HTMLElement).style.overflowY = "auto";
      (popover as HTMLElement).style.zIndex = "999";
    });
  };

  const reanchorBookingDetails = useCallback(() => {
    const bookingId = selectedBookingIdRef.current;
    if (!bookingId || !calendarRef.current) return;
    try {
      const api = (calendarRef.current as { getApi: () => { getEventById: (id: string) => { el?: HTMLElement | null } | null } }).getApi();
      const event = api.getEventById(String(bookingId));
      if (event?.el) {
        setAnchorEl(event.el);
      }
    } catch {
      // Calendar not ready yet.
    }
  }, []);

  const handleCloseDetails = () => {
    selectedBookingIdRef.current = null;
    skipDetailsCloseRef.current = false;
    setAnchorEl(null);
    setEditDetails(null);
  };

  const handleDetailsMenuClose = () => {
    if (skipDetailsCloseRef.current) return;
    handleCloseDetails();
  };

  const hideDetailsPopover = () => {
    selectedBookingIdRef.current = null;
    skipDetailsCloseRef.current = false;
    setAnchorEl(null);
  };

  const checkAvailability = (
    doctor: SearchParamsDoctorType,
    start: string,
    end: string
  ) => {
    let notification: { message: string; variant: any } | null = null;
    const { timeslots, doctorName } = doctor;
    const startTime = moment.utc(start);
    const endTime = moment.utc(end);
    const startTimeObj = timeslots.find(
      (day: any) => day.dayId == startTime.day()
    ) as any;
    const endTimeObj = timeslots.find(
      (day: any) => day.dayId == endTime.day()
    ) as any;

    if (!Boolean(startTimeObj) || !Boolean(endTimeObj)) {
      notification = {
        message: `Dr. ${doctorName} is not available for this time !`,
        variant: "warning",
      };
      return notification;
    }
    const startDate = moment.utc(
      `${moment.utc(start).format("YYYY-MM-DD")}T${startTimeObj.startTime}Z`
    );
    const endDate = moment.utc(
      `${moment.utc(start).format("YYYY-MM-DD")}T${endTimeObj.endTime}Z`
    );
    const startValidation = startTime.isBefore(startDate);
    const endValidation = endTime.isAfter(endDate);
    if (startValidation || endValidation) {
      startValidation &&
        endValidation &&
        (notification = {
          message: `Dr. ${doctorName} is available from ${startDate.format("DD-MM-YYYY hh:mm A")} to ${endDate.format("DD-MM-YYYY hh:mm A")}.`,
          variant: "info",
        });
      endValidation &&
        (notification = {
          message: `Dr ${doctorName} is not available from ${endDate.format("DD-MM-YYYY hh:mm A")}`,
          variant: "info",
        });
      startValidation &&
        (notification = {
          message: `Dr. ${doctorName} is available from ${startDate.format("DD-MM-YYYY hh:mm A")}.`,
          variant: "info",
        });
    }
    return notification;
  };

  const handleCopySave = (selectInfo: DateSelectArg) => {
    const params = prepareBookingParams({
      ...editDetails,
      visitType: { id: editDetails?.visitType?.visitType },
    } as BookingFormType);
    params.id = null;
    if (
      selectInfo.view.type == "dayGridMonth" ||
      selectInfo.view.type == "multiMonthYear"
    ) {
      const start = params.appStartTime.split("T")[1];
      const end = params.appEndTime.split("T")[1];
      params.appStartTime = `${selectInfo.startStr}T${start}`;
      params.appEndTime = `${selectInfo.startStr}T${end}`;
      save(params);
    } else {
      params.appStartTime = selectInfo.startStr;
      params.appEndTime = selectInfo.endStr;
      save(params);
    }
  };

  const handleDateSelect = useCallback(
    (selectInfo: DateSelectArg) => {
      let isBefore =
        selectInfo.view.type == "dayGridMonth" ||
        selectInfo.view.type == "multiMonthYear"
          ? moment(selectInfo.startStr)
              .startOf("day")
              .isBefore(moment().startOf("day"))
          : moment(selectInfo.startStr).isBefore(
              moment().format("YYYY-MM-DDTHH:mm:ss[Z]"),
              "minutes"
            );
      if (bookingSearchParam.doctor) {
        const { timeslots, doctorName } =
          bookingSearchParam.doctor as SearchParamsDoctorType;
        const startTime = moment.utc(selectInfo.startStr);
        const startTimeObj = timeslots.find(
          (day: any) => day.dayId == startTime.day()
        ) as any;
        if (!Boolean(startTimeObj)) {
          showSnackbar({
            message: `Dr. ${doctorName} is not available for this date !`,
            variant: "info",
            open: true,
            duration: 4000,
            origin: { vertical: "bottom", horizontal: "left" },
            direction: "right",
          });
          return;
        }
      }

      if (
        bookingSearchParam.doctor &&
        selectInfo.view.type != "dayGridMonth" &&
        selectInfo.view.type != "multiMonthYear"
      ) {
        const dateWarning = checkAvailability(
          bookingSearchParam.doctor,
          selectInfo.startStr,
          selectInfo.endStr
        );
        if (dateWarning) {
          showSnackbar({
            ...dateWarning,
            open: true,
            duration: 4000,
            origin: { vertical: "bottom", horizontal: "left" },
            direction: "right",
          });
          return;
        }
      }

      if (isBefore ) {
        const isMonthView =
    selectInfo.view.type === "dayGridMonth" ||
    selectInfo.view.type === "multiMonthYear";
        showSnackbar({
          message:  isMonthView?"Appointment Date should be greater than current time":"Appointment time should be greater than current time",
          open: true,
          variant: "warning",
          duration: 4000,
          origin: { vertical: "bottom", horizontal: "left" },
          direction: "right",
        });
      } else {
        setTimeSlot({
          slotType: selectInfo.view.type,
          endTime:
            selectInfo.view.type == "dayGridMonth" ||
            selectInfo.view.type == "multiMonthYear"
              ? null
              : selectInfo.endStr,
          startTime: selectInfo.startStr,
          times: "",
          timeSlotNo: 0,
        });
        console.clear();
        console.log("copyString---------->", copyString);
        console.log("copy---------->", copy);
        Boolean(copy) ? handleCopySave(selectInfo) : setOpen(true);
      }
    },
    [copy]
  );

  const handleEventClick = (selectedEvent: EventClickArg) => {
    if (Boolean(editDetails)) {
      selectedBookingIdRef.current = null;
      setEditDetails(null);
      setAnchorEl(null);
    } else {
      const data = selectedEvent.event.extendedProps.data;
      selectedBookingIdRef.current = data?.id ?? selectedEvent.event.id;
      setEditDetails({
        ...selectedEvent.event.extendedProps.data,
        start: selectedEvent.event.startStr,
        end: selectedEvent.event.endStr,
        timeSlot: selectedEvent.event.extendedProps.data.timeSlot,
        isRegistered:selectedEvent?.event.extendedProps?.data?.isRegistered,
        bookingConfirmed:selectedEvent?.event.extendedProps?.data?.bookingConfirmed,
        countryCode: buildCountryCodeFromBooking(
          selectedEvent.event.extendedProps.data,
        ),

      } as BookingFormType);
      setAnchorEl(selectedEvent.el);
    }
    // const morePopover = document.querySelector('.fc-more-popover');
    // if (morePopover) {
    //   morePopover.style.display = 'none';
    // }
  };

  const handleEventDrop = (selectedEvent: EventDropArg) => {
    updateFn = () => {
      bookingUpdate({
        bookingId: parseInt(selectedEvent.event.id),
        doctorId: selectedEvent.event.extendedProps.data.doctor.doctorId,
        startBookingDate: selectedEvent.event.startStr,
        endBookingDate: selectedEvent.event.endStr,
      });
    };
    // updateFn();
    setConfirm(true);
  };

  const handleEventResize = (selectedEvent: any) => {
    bookingUpdate({
      bookingId: parseInt(selectedEvent.event.id),
      doctorId: selectedEvent.event.extendedProps.data.doctor.doctorId,
      startBookingDate: selectedEvent.event.startStr,
      endBookingDate: selectedEvent.event.endStr,
    });
  };

  const handleDrag = (dropInfo: DateSpanApi) => {
    const isValid = moment(dropInfo.startStr).isBefore();
    return !isValid;
  };

  const handleEdit = () => {
    hideDetailsPopover();
    setOpen(true);
  };

  const handleCopy = () => {
    hideDetailsPopover();
    setCopyOpen(true);
  };

  const handleCloseCopy = () => {
    setCopyOpen(false);
  };

  const handleStartCopy = (e: MouseEvent<HTMLButtonElement>) => {
    const copyType = e.currentTarget.getAttribute("data-operation") as CopyType;
    console.log(copyType);
    copyString = copyType;
    setCopy(copyType);
    setCopyOpen(false);
    setCopyId({
      departmentId: editDetails?.department?.departmentId as number,
      doctorId: editDetails?.doctor?.doctorId as number,
    });
  };

  const handleClose = () => {
    setOpen(false);
    hideDetailsPopover();
    // setEditDetails(null);
    setTimeSlot((prev) => ({
      ...prev,
      endTime: null,
      startTime: null,
      slotType: "",
    }));
  };

  const handleDelete = () => {
    bookingCancel(editDetails?.id as string);
  };

  const handleDateChange = (info: any) => {
    setRefetch(true);
    setBookingSearchParams((prev) => ({
      ...prev,
      startTime: info.startStr,
      endTime: info.endStr,
    }));
  };

  const handleEncounter = () => {
    navigate(
      `${base}${moduleRoutes.mainRoute}/${moduleRoutes.patientCare}/${patientCareRouteList.patientEnounter}/${editDetails?.patientId}?bookingId=${editDetails?.id}`
    );
  };
  const handleRegister = () => {
    navigate(
      `${base}${moduleRoutes.mainRoute}/${moduleRoutes.patientCare}/${patientCareRouteList.patientRegistration}/0?bookingId=${editDetails?.id}`
    );
  };

  const showAge = (age: string) => {
    const calculated = calculateAge(age);
    const ageTypes = {
      D: "days",
      D1: "day",
      M: "months",
      M1: "month",
      Y: "years",
      Y1: "year",
    };
    const ageType =
      calculated.age == 1
        ? ageTypes[
            `${calculated.ageType}${calculated.age}` as keyof typeof ageTypes
          ]
        : ageTypes[calculated.ageType];
    return `${calculated.age} ${ageType}`;
  };

const handlePatientsearch = useCallback(
  (e: React.ChangeEvent<HTMLInputElement>) => {
    const search = e.target.value.toLowerCase().trim();

    if (!search) {
      setBookingList(backupArray.length ? backupArray : bookingList);
      setBackupArray([]);
      return;
    }

    if (!backupArray.length) {
      setBackupArray(bookingList);
    }

    const filtered = bookingList.filter((item: BookingListType) => {
      const data = item.data;

      const fullName = `${data.firstName ?? ""} ${data.lastName ?? ""}`.toLowerCase();
      const mobile = String(data.mobileNumber ?? "");
      const patientId = String(data.patientId ?? "");
      const doctorName = data.doctor?.doctorName?.toLowerCase() ?? "";
      const departmentName = data.department?.departmentName?.toLowerCase() ?? "";
      const email = data.email?.toLowerCase() ?? "";

      return (
        fullName.includes(search) ||
        mobile.includes(search) ||
        patientId.includes(search) ||
        doctorName.includes(search) ||
        departmentName.includes(search) ||
        email.includes(search)
      );
    });

    setBookingList(filtered);
  },
  [bookingList, backupArray]
);

  useEffect(() => {
    if (
      refetch ||
      Boolean(bookingSearchParam.startTime && bookingSearchParam.endTime)
    ) {
      fetchAllBooking({
        ...bookingSearchParam,
        doctorId: bookingSearchParam.doctor?.doctorId || null,
        departmentId: bookingSearchParam.department?.departmentId || null,
      });
    }
  }, [refetch, bookingSearchParam]);

  useEffect(() => {
    if (!skipDetailsCloseRef.current) return;
    const frameId = requestAnimationFrame(() => {
      reanchorBookingDetails();
      skipDetailsCloseRef.current = false;
    });
    return () => cancelAnimationFrame(frameId);
  }, [bookingList, reanchorBookingDetails]);

  return (
    <div className="px-2 py-3">
      <Snackbar
        message="Do you want to stop copying ?"
        action={
          <Button
            onClick={() => {
              setCopy(null);
              setCopyId({ departmentId: null, doctorId: null });
            }}
          >
            stop
          </Button>
        }
        open={copy == "multi"}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
      <Snackbar
        open={confirm}
        message="Do you want to change the appointment ?"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        action={
          <div className="flex gap-2">
            <Button
              color={"error"}
              onClick={() => {
                setConfirm(false);
                setRefetch(true);
              }}
            >
              no
            </Button>
            <Button color={"success"} onClick={updateFn}>
              yes
            </Button>
          </div>
        }
      />
      <Paper>
        {bookingLoading ? (
          <LinearProgress color="primary" />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 4,
              backgroundColor: theme.palette.primary.main,
            }}
          />
        )}
        <div className="p-2">
          {/* {copy == "multi" && (
            <div className="w-full flex justify-end ps-1 pe-7">
              <Button
                color="info"
                onClick={() => {
                  setCopy(null);
                }}
              >
                stop copying
              </Button>
            </div>
          )} */}
          <div className="pb-2 grid grid-cols-12 gap-2">
            <div className="col-span-2 flex justify-between items-center">
              <Typography color={"primary"} fontSize={"1rem"} fontWeight={600}>
                Patient Booking
              </Typography>
            </div>
            <div className="col-span-3">
              <TextField label="Search" onChange={handlePatientsearch} />
            </div>
          </div>
          <FullCalendar
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              listPlugin,
              momentPlugin,
              multiMonthPlugin,
            ]}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right:
                "multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay,listWeek",
            }}
            timeZone="UTC"
            slotDuration={
              durations[
                `${bookingSearchParam?.doctor?.duration || 12}` as keyof typeof durations
              ] || "00:05"
            }
            events={bookingList}
            initialView="dayGridMonth"
            slotMinTime={"07:00:00"}
            editable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={2}
            droppable={true}
            eventDurationEditable={false}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            height="calc(100vh - 138px)"
            eventAllow={handleDrag}
            datesSet={handleDateChange}
            eventResizeStop={handleEventResize}
            slotLaneClassNames={() =>
              Boolean(copy)
                ? saveLoading
                  ? "cursor-wait"
                  : "cursor-copy"
                : "cursor-pointer"
            }
            dayCellDidMount={handleDayCellDidMount}
          />
        </div>
      </Paper>
      <div className="cursor-pointer"></div>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleDetailsMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflowY: "auto",
            zIndex: 99999,
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <div className="w-[20vw] h-fit flex flex-col">
          <div className="py-1 px-2 border-b flex justify-between items-center">
            <div className="flex items-center gap-2 ps-2">
              {/* <PersonIcon /> */}
              <ListItemText
                primary={
                  <div className="px-2">{`${editDetails?.firstName} ${editDetails?.middleName || ""} ${editDetails?.lastName}`}</div>
                }
                secondary={
                  <div>
                    <PhoneIphoneIcon /> {`${editDetails?.mobileNumber}`}
                  </div>
                }
                sx={{ fontSize: "1rem", fontWeight: 600 }}
              />
              {editDetails?.patientId && (
                <span>({editDetails?.patientId})</span>
              )}
            </div>
            <IconButton size="small" color="error" onClick={handleCloseDetails}>
              <CancelIcon />
            </IconButton>
          </div>
          <div className="p-2">
            <Table aria-label="simple table">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 0 }}>
                    <strong>Start time</strong>
                  </TableCell>
                  <TableCell sx={{ border: 0 }}>
                    :{" "}
                    {moment(
                      editDetails?.startDate,
                      "YYYY-MM-DDTHH:mm:ss"
                    ).format("DD-MM-YYYY hh:mm A")}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0 }}>
                    <strong>End time</strong>
                  </TableCell>
                  <TableCell sx={{ border: 0 }}>
                    :{" "}
                    {moment(editDetails?.endDate, "YYYY-MM-DDTHH:mm:ss").format(
                      "DD-MM-YYYY hh:mm A"
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0 }}>
                    <strong>Age</strong>
                  </TableCell>
                  <TableCell sx={{ border: 0 }}>
                    : {showAge(editDetails?.dob as string)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0 }}>
                    <strong>Gender</strong>
                  </TableCell>
                  <TableCell sx={{ border: 0 }}>
                    : {editDetails?.gender}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Doctor</strong>
                  </TableCell>
                  <TableCell>: {editDetails?.doctor?.doctorName}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-between items-center gap-2 px-2">
            {editDetails?.isCompleted == 2 ? (
              <div className="ps-3">
                <Tooltip title="Encounter completed">
                  <DoneAllIcon sx={{ color: theme.palette.success.main }} />
                </Tooltip>
              </div>
            ) : editDetails?.patientId ? (
              <Button onClick={handleEncounter}>encounter</Button>
            ) : (
              <Button onClick={handleRegister} color="success">
                register
              </Button>
            )}
            <div className="flex gap-2">
              <Tooltip title="Edit appointment">
                <IconButton
                  sx={{ margin: 0 }}
                  color="success"
                  onClick={handleEdit}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy appointment">
                <IconButton
                  sx={{ margin: 0 }}
                  color="primary"
                  onClick={handleCopy}
                >
                  <FileCopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel appointment">
                <IconButton
                  disabled={cancelLoading}
                  onClick={handleDelete}
                  sx={{ margin: 0 }}
                  color="error"
                >
                  {cancelLoading ? (
                    <CircularProgress size={18} color="error" />
                  ) : (
                    <DeleteIcon />
                  )}
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
      </Menu>

      <CustomModal
        title="Patient booking"
        open={open}
        setOpen={setOpen}
        handleClose={handleClose}
      >
      <div className="p-3 xl:max-h-none max-h-[80vh] xl:overflow-visible overflow-y-auto">
          <BookingForm />
        </div>
      </CustomModal>

      <CustomModal
        title="Copy appointment"
        open={copyOpen}
        setOpen={setCopyOpen}
        handleClose={handleCloseCopy}
      >
        <div className="p-4 flex gap-3">
          Copy this appointment as
          <Button data-operation={"single"} onClick={handleStartCopy}>
            Single
          </Button>
          or
          <Button data-operation={"multi"} onClick={handleStartCopy}>
            Multiple
          </Button>
        </div>
      </CustomModal>
    </div>
  );
}

export default CalenderComponent;

// if (title) {
//   calendarApi.addEvent({
//     id: createEventId(),
//     title,
//     color: "blue",
//     start: selectInfo.startStr,
//     end: selectInfo.endStr,
//     allDay: selectInfo.allDay,
//     data: {
//       id: createEventId(),
//       name: title,
//       doctor: "test doctor",
//     },
//   });
// }
const durations = {
  "60": "00:01",
  "30": "00:02",
  "20": "00:03",
  "15": "00:04",
  "12": "00:05",
  "10": "00:06",
  "6": "00:10",
  "5": "00:12",
  "4": "00:15",
  "3": "00:20",
  "2": "00:30",
  "1": "00:60",
};
